import React from 'react';
import { SelectFactory, SelectTypes, Option } from '@g17eco/molecules/select/SelectFactory';
import { Col, Label, Row } from 'reactstrap';
import { AggregationMode, aggregationTypes, UtrValueType, ValueAggregation } from '@g17eco/core';
import { AggregationConfigOverride, ValueAggregationOverride } from '@g17eco/types/initiativeUniversalTracker';
import { TableAggregationType, type TableColumn, type TableGroupColumn } from '@g17eco/types/universalTracker';

interface Props {
  valueType: UtrValueType | undefined;
  childrenOptions: Option<ValueAggregationOverride>[];
  combinedOptions: Option<ValueAggregationOverride>[];
  aggregationConfig?: AggregationConfigOverride;
  tableColumns: TableColumn[] | undefined;
  onChange: (aggregationConfig: AggregationConfigOverride) => void;
  className?: string;
  isDisabled?: boolean;
}

const checkIsTableRowGroupAggregator = (
  props: Pick<Props, 'aggregationConfig' | 'combinedOptions' | 'childrenOptions'>,
) => {
  const { aggregationConfig, combinedOptions, childrenOptions } = props;
  const combinedValue = aggregationConfig?.modes?.combined?.valueAggregation;
  const childrenValue = aggregationConfig?.modes?.children?.valueAggregation;

  if (combinedValue !== 'default' || childrenValue !== 'default') {
    return (
      combinedValue === ValueAggregation.TableRowGroupAggregator &&
      childrenValue === ValueAggregation.TableRowGroupAggregator
    );
  }
  // check the option's `label` as `value` is hard to tell ("default")
  const defaultCombinedOption = combinedOptions.find((op) => op.value === 'default');
  const defaultChildrenOption = childrenOptions.find((op) => op.value === 'default');

  const regex = new RegExp(aggregationTypes[ValueAggregation.TableRowGroupAggregator], 'i');

  return regex.test(String(defaultCombinedOption?.label)) && regex.test(String(defaultChildrenOption?.label));
};

const GroupColumnsDropdown = (props: {
  tableColumns: TableColumn[];
  selectedColumns: TableGroupColumn[] | undefined;
  onChange: (columnCodes: string[]) => void;
}) => {
  const { tableColumns, selectedColumns, onChange } = props;
  const groupColumnOptions = tableColumns.map((col) => ({
    label: col.name,
    value: col.code,
  }));

  return (
    <>
      <Label className='mb-0' style={{ fontWeight: 500 }}>
        Table row grouping: Columns
      </Label>
      <Label>Groups rows by one or more column values</Label>
      <SelectFactory
        selectType={SelectTypes.MultipleSelect}
        options={groupColumnOptions}
        values={selectedColumns ? selectedColumns.map((col) => col.code) : []}
        onChange={onChange}
        isMenuPortalTargetBody
        menuPlacement='bottom'
        placeholder='Select group by columns'
      />
    </>
  );
};

export const AggregationConfigDropdown = ({
  valueType,
  childrenOptions,
  combinedOptions,
  aggregationConfig,
  tableColumns,
  onChange,
  className,
  isDisabled = false,
}: Props) => {
  const childrenValue = aggregationConfig?.modes?.children?.valueAggregation;
  const combinedValue = aggregationConfig?.modes?.combined?.valueAggregation;
  const isTableRowGroupAggregator = checkIsTableRowGroupAggregator({
    aggregationConfig,
    combinedOptions,
    childrenOptions,
  });

  if (valueType === UtrValueType.Table) {
    const handleChangeTableConfig = (option: Option<ValueAggregationOverride> | null) => {
      if (!option) {
        return;
      }
      const newSettings: AggregationConfigOverride = {
        ...aggregationConfig,
        modes: {
          ...aggregationConfig?.modes,
          children: {
            ...aggregationConfig?.modes?.children,
            valueAggregation: option?.value,
          },
          combined: {
            ...aggregationConfig?.modes?.combined,
            valueAggregation: option?.value,
          },
        },
      };
      onChange(newSettings);
    };

    const handleChangeTableGroupColumns = (columnCodes: string[]) => {
      const newSettings: AggregationConfigOverride = {
        ...aggregationConfig,
        table: {
          aggregation: {
            type: TableAggregationType.Group,
            columns: columnCodes.map((code) => ({ code })),
          },
        },
      };
      onChange(newSettings);
    };

    // all children options are in combined options, 
    // so we can use them as table aggregation options between both modes
    const tableAggregationOptions = childrenOptions;

    return (
      <Row className={className}>
        <Col cols={6}>
          <Label className='mb-0' style={{ fontWeight: 500 }}>Combined / Children mode</Label>
          <Label>Table aggregation is applied to both modes</Label>
          <SelectFactory<ValueAggregationOverride>
            selectType={SelectTypes.SingleSelect}
            options={tableAggregationOptions}
            value={tableAggregationOptions.find((op) => op.value === combinedValue) ?? null}
            onChange={handleChangeTableConfig}
            isDisabled={isDisabled}
            isMenuPortalTargetBody
            menuPlacement='bottom'
          />
        </Col>
        <Col cols={6}>
          {isTableRowGroupAggregator && tableColumns ? (
            <GroupColumnsDropdown
              tableColumns={tableColumns}
              selectedColumns={aggregationConfig?.table?.aggregation?.columns}
              onChange={handleChangeTableGroupColumns}
            />
          ) : null}
        </Col>
      </Row>
    );
  }

  const handleChange = (option: Option<ValueAggregationOverride> | null, mode: AggregationMode) => {
    const newSettings: AggregationConfigOverride = {
      ...aggregationConfig,
      modes: {
        ...aggregationConfig?.modes,
        [mode]: {
          ...aggregationConfig?.modes?.[mode],
          valueAggregation: option?.value,
        },
      },
    };
    onChange(newSettings);
  };

  return (
    <Row className={className}>
      <Col cols={6}>
        <Label style={{ fontWeight: 500 }}>Children mode</Label>
        <SelectFactory
          selectType={SelectTypes.SingleSelect}
          options={childrenOptions}
          value={childrenOptions.find((op) => op.value === childrenValue) ?? null}
          onChange={(op) => handleChange(op, AggregationMode.Children)}
          isDisabled={isDisabled}
          isMenuPortalTargetBody
          menuPlacement='bottom'
        />
      </Col>
      <Col cols={6}>
        <Label style={{ fontWeight: 500 }}>Combined mode</Label>
        <SelectFactory
          selectType={SelectTypes.SingleSelect}
          options={combinedOptions}
          value={combinedOptions.find((op) => op.value === combinedValue) ?? null}
          onChange={(op) => handleChange(op, AggregationMode.Combined)}
          isDisabled={isDisabled}
          isMenuPortalTargetBody
          menuPlacement='bottom'
        />
      </Col>
    </Row>
  );
};
